import { useTranslation } from "react-i18next";
import React, { useMemo } from "react";
import StatusBadge from "../../../../components/StatusBadge";
import Loading from "../../../../components/Loading";
import { useQuery } from "react-query";
import { apollo } from "../../../../utils/apollo";
import { GET_SLASHDATA_POLICY_MEMBERS } from "../../../../utils/graphql";
import InformationColumn from "../../../../components/InformationColumn";
import ButtonInternalLink from "../../../../components/ButtonInternalLink";
function SlashdataPolicyView({ loading, policy }) {
  const { t } = useTranslation();
  const policyAlreadyExists = ["already_exists", "AlreadyExists"].includes(
    policy?.policiable?.integrationStatus,
  );
  const highlightTextColor = policyAlreadyExists ? "text-blue-700" : null;
  if (loading || !policy) return <Loading />;
  return (
    <div className="mt-6 border-t border-slate-100">
      <div className="space-y-8">
        {/* Policy Details Section */}
        <div className="pt-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <InformationColumn
              title={t("Policy Uuid")}
              value={policy?.policyUuid}
            />
            <InformationColumn
              title={t("Ic Request Ref No")}
              value={policy?.policiable?.icRequestRefNo}
            />
            <InformationColumn
              title={t("Policy Number")}
              value={policy?.policiable?.policyNumber}
            />
            <InformationColumn
              title={t("Policy Issue Date")}
              value={policy?.policiable?.policyIssueDate}
              textColor={highlightTextColor}
            />
            <InformationColumn
              title={t("Policy Start Date")}
              value={policy?.policiable?.policyStartDate}
              textColor={highlightTextColor}
            />
            <InformationColumn
              title={t("Policy Expiry Date")}
              value={policy?.policiable?.policyExpiryDate}
              textColor={highlightTextColor}
            />
            <InformationColumn
              title={t("Policy Type")}
              value={policy?.policiable?.policyType}
            />
            <InformationColumn
              title={t("Policy Owner Type")}
              value={policy?.policiable?.policyOwnerType}
            />
            <InformationColumn
              title={t("Policy Owner Name")}
              value={policy?.policiable?.policyOwnerName}
            />
            <InformationColumn
              title={t("Policy Owner Id")}
              value={policy?.policiable?.policyOwnerId}
            />
            <InformationColumn
              title={t("Plan Name")}
              value={policy?.policiable?.planName}
            />
            <InformationColumn
              title={t("Operation Type")}
              value={policy?.policiable?.operationType}
            />
            <InformationColumn
              title={t("Policy Creation Ref No")}
              value={policy?.policiable?.policyCreationRefNo}
              textColor={highlightTextColor}
            />
            <InformationColumn
              title={t("Policy Cancellation Ref No")}
              value={policy?.policiable?.policyCancellationRefNo}
            />
            <InformationColumn
              title={t("Cancellation Date")}
              value={policy?.policiable?.cancellationDate}
            />
            <InformationColumn
              title={t("Integration Status")}
              value={
                <StatusBadge status={policy?.policiable?.integrationStatus} />
              }
            />
          </div>
        </div>

        {policyAlreadyExists && (
          <div className="flex gap-4 p-5 rounded-2xl border border-blue-100 bg-blue-50/50 shadow-sm transition-all duration-300">
            <p className="text-sm font-medium leading-relaxed text-blue-800">
              {t(
                "The highlighted blue text represents values extracted from the Slashdata platform, which are associated with an existing policy.",
              )}
            </p>
          </div>
        )}

        <SlashdataPolicyMembers policy={policy} />
      </div>
    </div>
  );
}
function SlashdataPolicyMembers({ policy }) {
  const { t } = useTranslation();
  const fetchSlashdataPolicyMembers = useQuery({
    queryKey: ["slashdata_policy_member_list", policy.policyUuid],
    queryFn: async () => {
      const { data } = await apollo.query({
        query: GET_SLASHDATA_POLICY_MEMBERS,
        variables: {
          policyUuid: policy.policyUuid,
        },
      });
      return data?.getSlashdataPolicyMembers;
    },
    keepPreviousData: false,
  });
  const members = useMemo(
    () =>
      fetchSlashdataPolicyMembers?.isSuccess &&
        !fetchSlashdataPolicyMembers?.isLoading
        ? fetchSlashdataPolicyMembers?.data
        : null,
    [fetchSlashdataPolicyMembers],
  );
  if (fetchSlashdataPolicyMembers.isLoading)
    return (
      <div className="py-12">
        <Loading message={t("Loading members...")} />
      </div>
    );
  if (!members?.length) return null;
  return (
    <div className="mt-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-8 w-1.5 bg-primary-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]" />
        <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
          {t("Policy Members")}
        </h3>
        <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full ms-2">
          {members.length} {t("Total")}
        </span>
      </div>

      <div className="flow-root">
        <div className="overflow-auto pb-16 rounded-2xl border border-slate-200/60 bg-white shadow-sm">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50/50">
                <tr>
                  <th
                    scope="col"
                    className="py-4 ps-4 pe-3 text-start text-[11px] font-black uppercase tracking-wider text-slate-500 sm:ps-6"
                  >
                    <span className="sr-only">{t("Edit")}</span>
                  </th>
                  {[
                    "FullNameEn",
                    "IntegrationStatus",
                    "Status",
                    "Errors",
                    "FirstNameEn",
                    "MiddleNameEn",
                    "LastNameEn",
                    "FullNameAr",
                    "FirstNameAr",
                    "MiddleNameAr",
                    "LastNameAr",
                    "EnrolmentIssueDate",
                    "EnrolmentStartDate",
                    "UnifiedNo",
                    "EmiratesIdNo",
                    "VisaFileNo",
                    "BirthCertNo",
                    "PassportNo",
                    "Gender",
                    "NationalityCode",
                    "DateOfBirth",
                    "MaritalStatus",
                    "RelationWithSponsor",
                    "MemberIdType",
                    "MemberType",
                    "SponsorIdNo",
                    "SponsorIdType",
                    "MembershipCardNo",
                    "ClassName",
                    "SalaryBand",
                    "OccupationDesc",
                    "EmiratesOfVisaCode",
                    "EmiratesOfLivingCode",
                    "MemberCreationRefNo",
                    "MemberCancelRefNo",
                    "CancellationDate",
                    "CreatedAt",
                    "UpdatedAt",
                  ].map((header) => (
                    <th
                      key={header}
                      scope="col"
                      className="px-3 py-4 text-start text-[11px] font-black uppercase tracking-wider text-slate-500"
                    >
                      {t(header)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {members.map((member) => (
                  <tr
                    key={member.id}
                    className="hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="whitespace-nowrap py-4 ps-4 pe-3 text-sm font-medium sm:ps-6">
                      <ButtonInternalLink
                        href={`/member/edit/slashdata/${member?.id}`}
                        ghost
                        className="text-primary-600 hover:text-primary-700 !px-3 !py-1.5"
                      >
                        {t("Edit")}
                      </ButtonInternalLink>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-600 font-bold">
                      {member.fullNameEn}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <StatusBadge status={member.integrationStatus} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.status}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-red-500 font-medium">
                      {member.errors}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.firstNameEn}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.middleNameEn}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.lastNameEn}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.fullNameAr}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.firstNameAr}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.middleNameAr}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.lastNameAr}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.enrolmentIssueDate}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.enrolmentStartDate}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500 font-mono">
                      {member.unifiedNo}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500 font-mono">
                      {member.emiratesIdNo}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.visaFileNo}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.birthCertNo}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.passportNo}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.gender}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.nationalityCode}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500 font-mono">
                      {member.dateOfBirth}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.maritalStatus}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.relationWithSponsor}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.memberIdType}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.memberType}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500 font-mono">
                      {member.sponsorIdNo}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.sponsorIdType}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.membershipCardNo}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.className}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.salaryBand}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.occupationDesc}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.emiratesOfVisaCode}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.emiratesOfLivingCode}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.memberCreationRefNo}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.memberCancelRefNo}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                      {member.cancellationDate}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-400">
                      {member.createdAt}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-400">
                      {member.updatedAt}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
export default SlashdataPolicyView;
